import { t } from 'i18next';
import { useMemo } from 'react';
import { cn } from '../../../lib/styles';
import { FieldError } from '../field-error/field-error.spark';
import { Icon } from '../icon/icon.spark';
import { Text } from '../text/text.spark';
import { Tooltip } from '../tooltip/tooltip.spark';
import { useFieldIds } from '../use-field-ids';
import type { InputProps } from './types';

export function Input({
  label,
  tooltip,
  short,
  description,
  prefix,
  suffix,
  onChange,
  id,
  error,
  icon,
  ...inputProps
}: InputProps) {
  // NOTE: this length counter only works when manually managing the state and passing through `value`
  // Without passing `value` through, this component won't re-render, so won't update the count
  const valueLength = useMemo(() => inputProps.value?.length ?? 0, [inputProps.value?.length]);

  const { minLength, maxLength } = inputProps;

  // Native `minLength` validation ignores empty values, so an untouched field isn't yet too short
  const shortfall = minLength && valueLength > 0 ? minLength - valueLength : 0;
  const belowMinLength = shortfall > 0;

  const {
    fieldId: computedId,
    descriptionId,
    charCountId,
    errorId,
    describedBy: describedByIds,
  } = useFieldIds({
    id,
    name: inputProps.name,
    hasDescription: !!description,
    hasCharCount: !!maxLength,
    hasError: !!error,
  });

  return (
    <div className="flex flex-col gap-1.5 text-sm text-spark-text-primary">
      {/* Label & tooltip */}
      {label && (
        <label htmlFor={computedId} className="flex items-center gap-x-1">
          {label}
          {tooltip && (
            <Tooltip content={tooltip}>
              <Icon name="infoCircle" />
            </Tooltip>
          )}
        </label>
      )}
      <div
        className={cn(
          'flex h-spark-component shrink-0 items-center gap-1 rounded-lg border border-spark-border-default bg-spark-surface px-2 text-spark-text-secondary shadow-xs transition focus-within:border-spark-primary-accent focus-within:outline-hidden focus-within:ring-spark-primary-accent',
          short ? 'w-32' : 'w-full',
          {
            'bg-spark-surface-muted': inputProps.disabled,
            'border-spark-danger-border bg-spark-danger-soft text-spark-text-danger focus-within:border-spark-text-danger':
              !!error,
          },
        )}
      >
        {icon && <Icon name={icon} />}
        {prefix && <span className="whitespace-nowrap">{prefix}</span>}
        <input
          {...inputProps}
          onChange={(e) => onChange?.(e.target.value)}
          id={computedId}
          aria-describedby={describedByIds || undefined}
          aria-invalid={!!error}
          className={cn(
            'form-input w-full border-0 bg-transparent p-0 py-1 text-sm text-spark-text-primary placeholder-spark-text-secondary outline-hidden focus:ring-0',
            {
              'text-spark-text-secondary': inputProps.disabled,
              'placeholder-spark-text-danger': !!error,
            },
          )}
        />
        {suffix && <span>{suffix}</span>}
        {/* Max length counter */}
        {maxLength && (
          <span
            id={charCountId}
            className={cn('flex justify-end', {
              'text-spark-text-danger': belowMinLength,
            })}
          >
            {valueLength}/{maxLength}
          </span>
        )}
      </div>

      {(minLength || maxLength) && (
        <div aria-live="polite" className="sr-only">
          {belowMinLength
            ? t('aria:characters_required', { number: shortfall })
            : maxLength && t('aria:characters_remaining', { number: maxLength - valueLength })}
        </div>
      )}

      {error && <FieldError id={errorId}>{error}</FieldError>}

      {description && (
        <div className="ms-0.5">
          <Text id={descriptionId} variant="secondary" size="sm">
            {description}
          </Text>
        </div>
      )}
    </div>
  );
}
